import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tr, useT } from "../../lib/i18n";
import { formatMoney } from "../../lib/settings";

export const GROUP = (status) => {
  if (status === "Available" || status === "Consignment") return "ready";
  if (status === "Reserved") return "reserved";
  if (status === "On Test Drive" || status === "Out for Inspection") return "out";
  if (status === "Sold") return "sold";
  return "prep";
};
export const GROUPS = ["ready", "reserved", "out", "prep", "sold"];

function useColumns(ref) {
  const [cols, setCols] = useState(30);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setCols(Math.max(1, Math.floor((e.contentRect.width - 8 + 5) / 25))));
    ro.observe(ref.current);
    return () => { ro.disconnect(); };
  }, [ref]);
  return cols;
}

function Tip({ car, rect, settings, showCost, rtl }) {
  if (!car || !rect) return null;
  const W = 266;
  const sb = document.querySelector('[data-testid="app-sidebar"]')?.getBoundingClientRect();
  const minL = !rtl && sb && sb.right < window.innerWidth / 2 ? sb.right + 8 : 8;
  const maxL = (rtl && sb && sb.left > window.innerWidth / 2 ? sb.left : window.innerWidth) - W - 8;
  const left = Math.min(maxL, Math.max(minL, rtl ? rect.right - W + 4 : rect.left - 4));
  return (
    <div className="lot-tip" style={{ left, top: rect.bottom + 8 }} data-testid="lot-tooltip" role="status">
      <div style={{ fontWeight: 600 }}>{car.make} {car.model} {car.model_year || ""}</div>
      <div className="num-display" style={{ fontSize: 16 }}><bdi>{car.stock_number}</bdi> · <bdi>{formatMoney(car.price || 0, settings)}</bdi></div>
      {showCost && car.cost != null && <div style={{ opacity: 0.8 }}>{tr("lot.cost")}: <bdi className="num-display">{formatMoney(car.cost, settings)}</bdi></div>}
      <div style={{ opacity: 0.8 }}>{tr(`lot.g_${car.group /* i18n-keys: ready,reserved,out,prep,sold */}`)}</div>
    </div>
  );
}

// One small tile per car; arrow keys move, Enter opens. Light DOM, one focus stop (aria-activedescendant).
export const LotBoard = ({ cars, settings, showCost, filter }) => {
  const nav = useNavigate();
  const { lang } = useT();
  const ref = useRef(null);
  const cols = useColumns(ref);
  const [active, setActive] = useState(-1);
  const [rect, setRect] = useState(null);
  const [reveal] = useState(() => { try { const s = !sessionStorage.getItem("lot_seen"); sessionStorage.setItem("lot_seen", "1"); return s; } catch { return false; } });
  const rows = Math.max(1, Math.ceil(cars.length / cols));
  const step = Math.min(60, 480 / rows);
  const show = (i, el) => { setActive(i); setRect(el ? el.getBoundingClientRect() : null); };
  const onKey = (e) => {
    const rtl = lang === "ar";
    const moves = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1, ArrowDown: cols, ArrowUp: -cols };
    if (e.key in moves) {
      e.preventDefault();
      const n = Math.min(cars.length - 1, Math.max(0, (active < 0 ? 0 : active + moves[e.key])));
      show(n, ref.current?.children[n]);
      ref.current?.children[n]?.scrollIntoView({ block: "nearest" });
    } else if (e.key === "Enter" && active >= 0) nav(`/inventory/${cars[active].id}`);
    else if (e.key === "Escape") show(-1, null);
  };
  const tiles = useMemo(() => cars.map((c, i) => (
    <div key={c.id} id={`lot-tile-${i}`} role="option" aria-selected={false} aria-label={`${c.make} ${c.model} ${c.stock_number}`}
      className={`lot-tile ${c.group} ${filter && !filter(c) ? "dim" : ""}`} style={reveal ? { "--d": `${Math.floor(i / cols) * step}ms` } : undefined}
      data-testid={`lot-tile-${c.stock_number}`} data-group={c.group}
      onMouseEnter={(e) => show(i, e.currentTarget)} onClick={() => nav(`/inventory/${c.id}`)} />
  // eslint-disable-next-line react-hooks/exhaustive-deps
  )), [cars, cols, filter, reveal]);
  return (
    <div onMouseLeave={() => show(-1, null)}>
      <div ref={ref} className={`lot-board ${reveal ? "lot-reveal" : ""}`} role="listbox" tabIndex={0} aria-label={tr("lot.board_label")}
        aria-activedescendant={active >= 0 ? `lot-tile-${active}` : undefined} onKeyDown={onKey} onBlur={() => show(-1, null)} data-testid="lot-board">
        {tiles}
      </div>
      {active >= 0 && <style>{`#lot-tile-${active}{transform:scale(1.25);box-shadow:0 0 0 2px #fff,0 0 0 4px var(--ds-ink)!important}`}</style>}
      <Tip car={cars[active]} rect={rect} settings={settings} showCost={showCost} rtl={lang === "ar"} />
    </div>
  );
};
